<template>
  <div class="aftersales">
    <listing-index-hero
      :title="page?.title"
      :abstract="page?.description" />

    <div class="aftersales__select l-grid">
      <input-select-native
        id="supportArea"
        v-model="selectedArea"
        class="aftersales__central-col"
        :placeholder="$t('forms.placeholder.select')"
        name="supportArea"
        :options="areas"
        sync />
    </div>

    <section
      v-if="activeData"
      :class="['aftersales__section', `-${selectedArea}`]">
      <div class="l-grid">
        <div class="aftersales__central-col block-spacing">
          <div class="aftersales__block-title">
            {{ activeData.title }}
          </div>
          <div
            class="aftersales__block-desc"
            v-html="activeData.description" />
        </div>
      </div>

      <div
        v-if="selectedArea == 'whiteGoods'"
        class="aftersales__full-col bg-gray">
        <block-brands />
      </div>
      <div
        v-else
        class="l-grid">
        <div
          v-if="exists(formFields)"
          class="aftersales__central-col">
          <form-builder
            handle="afterSalesSupport"
            :params="[{ key: 'request', value: selectedArea }]" />
        </div>
      </div>
    </section>

    <block-launch
      v-if="activeData && selectedArea == 'whiteGoods' && page?.bottomAbstract && activeData"
      :description="page?.bottomAbstract"
      :link="exists(page?.bottomLink) ? getFirst(page?.bottomLink) : null"
      :form-params="[{ key: 'request', value: 'whiteGoods' }]" />
  </div>
</template>

<script setup>
  import transitions from '~/animations/pages'
  import { AFTERSALES_FORM } from '~/graphql/forms/after-sales'
  import { AFTERSALES_SUPPORT } from '~/graphql/pages/after-sales-support'

  const { t, locale } = useI18n()
  const { bus } = useNuxtApp()
  const {
    previewToken, previewParam, previewTimestamp
  } = usePreview()
  const [ { data, error }, { data: form } ] = await Promise.all([
    useAsyncData(
      `after-sales-support-${locale.value}`,
      async () => await useApi(AFTERSALES_SUPPORT, {}, {
        previewToken: previewToken.value,
        previewParam: previewParam.value
      }), {
        transform: r => r.page,
        watch: [ previewTimestamp ]
      }
    ),
    useAsyncData(
      `after-sales-form-${locale.value}`,
      async () => await useApi(AFTERSALES_FORM), {
        transform: r => r.form
      }
    )
  ])

  // Handle fetch errors
  if (error.value || !data.value) {
    console.error('Error fetching after-sales data:', error.value)

    throw createError({
      statusCode: 404,
      statusMessage: 'Page Not Found'
    })
  }

  const page = ref(data?.value)
  const selectedArea = ref(null)

  const areas = computed(() => {
    return [
      { label: t('forms.afterSales.whiteGoods'), value: 'whiteGoods' },
      { label: t('forms.afterSales.furnitures'), value: 'furnitures' },
      { label: t('forms.afterSales.complements'), value: 'complements' }
    ]
  })

  const formFields = computed(() => {
    return form.value?.formFields.filter(f => !f.visibility)
  })

  const activeData = computed(() => {
    switch (selectedArea.value) {
    case 'whiteGoods':
      return {
        title: page?.value?.tab1Title,
        description: page?.value?.tab1Description
      }
    case 'furnitures':
      return {
        title: page?.value?.tab2Title,
        description: page?.value?.tab2Description
      }
    case 'complements':
      return {
        title: page?.value?.tab3Title,
        description: page?.value?.tab3Description
      }

    default:
      return null
    }
  })

  watch(() => selectedArea.value, async () => {
    await nextTick()
    bus.emit('scroll:refresh')
  }, { immediate: true })

  useSeoData(page)

  definePageMeta({
    pageTransition: {
      name: 'page-transition',
      mode: 'out-in',
      ...transitions
    }
  })
</script>

<style lang="scss" scoped>
  .aftersales {
    &__central-col {
      grid-column: 1 / span 12;

      @include mq(medium) {
        grid-column: 4 / span 6;
      }
    }

    &__full-col {
      grid-column: 1 / span 12;
    }

    &__central-col :deep(.form-loader .cta--solid.cta--white) {
      background-color: var(--color-red);
    }

    &__central-col :deep(.form-loader .form-loader__body) {
      grid-column: 1 / span 12;
    }

    &__central-col :deep(.form-loader) {
      width: 100%;
      padding: 0;
    }

    &__block-title {
      @include body-l;

      margin-bottom: rem(32px);
    }

    &__block-desc :deep(.text-big) {
      font-size: rem(22px);
    }
  }
</style>
